/* STENCIL IMPORTS */
import {
  Component,
  Element,
  getAssetPath,
  Host,
  Method,
  Prop,
  State,
  h,
  Watch
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";

import { config } from "../../common/config";
import { Locale } from "../../common/locale";
import {
  mapOptionsToComboBoxItemModel,
  renderChCheckboxItems
} from "../../common/helpers";
import { getSelectedItem, setSelectedItem } from "../../common/common";
import { FormSubmitResult, GxOption } from "../../common/types";
import {
  ControlValidation,
  validateControls
} from "../../common/form-validation";
import {
  ChRadioGroupRenderCustomEvent,
  TabModel,
  ComboBoxModel,
  ChEditCustomEvent,
  RadioGroupModel,
  ChCheckboxCustomEvent
} from "@genexus/chameleon-controls-library";
import { removeIndex } from "../../common/array";

const CSS_BUNDLES: MercuryBundles = [
  // TODO: review if "utils/form--full" is required.
  "resets/box-sizing",
  "components/tab",
  "components/tooltip",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const WARNING_ICON = getIconPath({
  category: "system",
  name: "warning",
  colorType: "on-interactive"
});

const MAIN_SECTION_CLASSES: string = "main";
const BASIC_SECTION_PARENT_CLASSES: string = "basic";
const BASIC_TAB_SELECTOR: string = "basic";
const ADVANCED_TAB_SELECTOR: string = "advanced";

@Component({
  tag: "gx-ide-new-environment",
  styleUrl: "new-environment.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/new-environment"]
})
export class GxIdeNewEnvironment {
  #advancedTabHasBeenRendered: boolean = false;
  #chShortcutsEl!: HTMLChShortcutsElement;
  #envNameModifiedByUser = false;
  /**
   * frontEndsState saves the id's of the front ends checkboxes that are checked
   */
  // TODO (Use a set instead of an array to improve performance)
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #frontEndsCheckedMap = new Map<string, string[]>();
  #selectedDataStoreId: string;
  #selectedLanguageId: string;
  #selectedRuntimeEnvironmentId: string;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/new-environment/shortcuts.json`
  );
  #newEnvironmentSectionsModel: TabModel = [];

  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  @Element() el: HTMLGxIdeNewEnvironmentElement;

  // References needed to collect data con "createCallback" button submit*/
  #dataStoreEl!: HTMLChComboBoxRenderElement;
  #environmentNameEl!: HTMLChEditElement;
  #languageEl!: HTMLChComboBoxRenderElement;
  #setAsCurrentEnvironmentEl!: HTMLChCheckboxElement;

  @State() advancedTabIsVisible: boolean = false;

  /**
   * Allows selecting multiple generators for the front end
   * TODO: Check if this property can be deleted, since we can call getFrontEndsCallback.
   */
  @State() frontEnds: GxOption[];

  @State() validatableControls = new Map<string, ControlValidation>();

  // representations of GxOption[] as ComboBoxModel or RadioGroupModel.
  @State() dataStoresComboBoxModel: ComboBoxModel = [];
  @State() languageComboBoxModel: ComboBoxModel = [];
  @State() runtimesRadioGroupModel: RadioGroupModel = [];

  /**
   * Callback invoked when user wants to cancel
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Callback invoked when user confirms the KB creation
   */
  @Prop() readonly createCallback!: CreateCallback;

  /**
   * Allows defining the DBMS to be used in the solution
   */
  @Prop({ mutable: true }) dataStores!: GxOption[];
  @Watch("dataStores")
  dataStoresChanged(newValue: GxOption[]) {
    if (newValue?.length) {
      this.dataStoresComboBoxModel = mapOptionsToComboBoxItemModel(newValue);
      const result = setSelectedItem(newValue, this.#selectedDataStoreId);
      this.#selectedDataStoreId = getSelectedItem(result);
    }
  }

  /**
   * Name of the new environment
   */
  @Prop({ mutable: true }) environmentName: string;

  /**
   * Callback invoked when user changes the language or the runtime
   */
  @Prop() readonly getDataSourcesCallback!: GetDataSourcesCallback;

  /**
   * Callback invoked when user changes the language or data source
   */
  @Prop() readonly getEnvironmentNameCallback!: GetEnvironmentNameCallback;

  /**
   * Callback invoked when user changes the language
   */
  @Prop() readonly getFrontEndsCallback!: GetFrontEndsCallback;

  /**
   * Possible values for 'Language' combo
   */
  @Prop({ mutable: true }) languages!: GxOption[];
  @Watch("languages")
  languagesChanged(newValue: GxOption[]) {
    if (newValue?.length) {
      this.languageComboBoxModel = mapOptionsToComboBoxItemModel(newValue);
      this.#selectedLanguageId = getSelectedItem(newValue);
      this.#updateFrontEnds();
    }
  }

  /**
   * Define if the Advanced tab should be displayed
   */
  @Prop() readonly isAdvanced: boolean = false;

  /**
   * When true, the KB is backend only, so no front ends can be selected. A
   * callout is shown below the front end checkboxes explaining this.
   */
  @Prop() readonly isBackendOnly: boolean = false;

  /**
   * Callback invoked when selected runtime is changed
   */
  @Prop() readonly getLanguagesCallback!: GetLanguagesCallback;

  /**
   * Possible values for 'Runtime Environment' radio button
   */
  @Prop() readonly runtimes!: GxOption[];
  @Watch("runtimes")
  runtimesChanged(newValue: GxOption[]) {
    if (newValue?.length) {
      this.runtimesRadioGroupModel = mapOptionsToComboBoxItemModel(
        this.runtimes
      );

      this.#updateLanguages();
    }
  }

  /**
   * Belongs to checkbox 'Set as current environment' @default true
   *
   */
  @Prop() readonly setAsTarget?: boolean = true;

  connectedCallback() {
    this.dataStoresChanged(this.dataStores);
    this.languagesChanged(this.languages);
    this.runtimesChanged(this.runtimes);
  }

  componentDidLoad() {
    this.#initializeValidatableControlsBasicTab();
  }

  componentDidRender() {
    if (this.advancedTabIsVisible && !this.#advancedTabHasBeenRendered) {
      // just call this once.
      this.#advancedTabHasBeenRendered = true;
    }
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#newEnvironmentSectionsModel = [
      {
        id: BASIC_TAB_SELECTOR,
        name: this.#componentLocale.tabBasic.tabLabel
      },
      {
        id: ADVANCED_TAB_SELECTOR,
        name: this.#componentLocale.tabAdvanced.tabLabel
      }
    ];
    this.#evaluateSelectedRuntimeEnvironmentId();
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  #dataStoreValueChangedHandler = () => {
    /* the data store item id is used for the value (they are equal)*/
    this.#selectedDataStoreId = this.#dataStoreEl.value;
    this.#updateEnvironmentName();
  };

  #envNameInputHandler = (e: ChEditCustomEvent<string> | InputEvent) => {
    const newEnvName = (e as ChEditCustomEvent<string>).detail.trim();
    this.#envNameModifiedByUser = newEnvName !== this.environmentName;
  };

  #evaluateSelectedRuntimeEnvironmentId = () => {
    this.#selectedRuntimeEnvironmentId = this.runtimes[0]?.value;
  };

  // TODO: We must avoid performing the get 3 times.
  // It is used on new-environment as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  #initializeValidatableControlsBasicTab = () => {
    // populate #controlsValidation with the controls that could have errors.
    const validatableControls: HTMLElement[] = [
      this.#dataStoreEl,
      this.#languageEl
    ];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #languageValueChangedHandler = async (
    event: CustomEvent<string> | InputEvent
  ) => {
    this.#selectedLanguageId = event.detail as string;
    await this.#updateDataSources();
    this.#updateFrontEnds();
    this.#updateEnvironmentName();
  };

  #renderAdvanced = (): Element => {
    return <slot name="property-grid"></slot>;
  };

  #renderBasic = (): HTMLElement[] => {
    return [
      <div
        class={{
          "basic__runtime-environment": true,
          "dialog-header-with-border": true,
          "one-runtime-only": this.runtimes.length === 1
        }}
      >
        {this.runtimes.length > 1
          ? [
              <h2
                // Runtime Environment
                class="subtitle-regular-xs	 text-align-center"
              >
                {this.#componentLocale.runtime}
              </h2>,
              <ch-radio-group-render
                class="radio-group"
                onChange={this.#runtimeEnvironmentChangedHandler}
                model={this.runtimesRadioGroupModel}
                value={this.#selectedRuntimeEnvironmentId}
              ></ch-radio-group-render>
            ]
          : [
              <h2 class="subtitle-regular-xs	 text-align-center">
                {this.#componentLocale.runtime}:
              </h2>,
              <p>{this.runtimesRadioGroupModel[0].caption}</p>
            ]}
      </div>,
      <section class="basic__back-end scrollable">
        <h2
          // back end
          class="dialog-header dialog-header-with-border dialog-header__back-end subtitle-regular-xs	 text-align-center spacing-body-inline"
        >
          {this.#componentLocale.tabBasic.backEnd.backEndLabel}
        </h2>

        <div class="field-group main__back-end spacing-body-inline scrollable">
          <div
            // #language
            class="field field-block"
          >
            <label class="label" htmlFor="language">
              {this.#componentLocale.tabBasic.backEnd.language}
            </label>
            <ch-combo-box-render
              id="language"
              class="combo-box"
              part="language"
              value={this.#selectedLanguageId}
              disabled={this.languages.length === 0}
              model={this.languageComboBoxModel}
              onInput={this.#languageValueChangedHandler}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#languageEl = el as HTMLChComboBoxRenderElement)
              }
              // TODO: toolTip={config.tooltip}
            ></ch-combo-box-render>
            {this.#evaluateTooltipRender(this.#languageEl)}
          </div>

          <div
            // #data stores
            class="field field-block"
          >
            <label class="label" htmlFor="data-store">
              {this.#componentLocale.tabBasic.backEnd.dataStores}
            </label>
            <ch-combo-box-render
              id="data-store"
              class="combo-box"
              part="data-store"
              value={this.#selectedDataStoreId}
              model={this.dataStoresComboBoxModel}
              onInput={this.#dataStoreValueChangedHandler}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#dataStoreEl = el as HTMLChComboBoxRenderElement)
              }
              // TODO: toolTip={config.tooltip}
            ></ch-combo-box-render>
            {this.#evaluateTooltipRender(this.#dataStoreEl)}
          </div>
        </div>
      </section>,

      <section class="basic__front-end scrollable">
        <h2
          // front end
          class="dialog-header dialog-header-with-border dialog-header__front-end subtitle-regular-xs	 text-align-center spacing-body-inline"
        >
          {this.#componentLocale.tabBasic.frontEnd.frontEndLabel}
        </h2>
        <div
          class="field-group front-end__checkboxes spacing-body-inline main__front-end scrollable"
          role="group"
          aria-labelledby={this.#componentLocale.tabBasic.frontEnd.generators}
        >
          {renderChCheckboxItems(
            this.frontEnds,
            this.#frontEndCheckboxInputHandler,
            this.#frontEndsCheckedMap.get(this.#selectedLanguageId)
          )}

          {this.isBackendOnly && (
            <div class="callout" part="backend-only-callout">
              <ch-image
                class="icon-l"
                src={WARNING_ICON}
                type="mask"
              ></ch-image>

              <div class="callout-inner">
                <span class="callout__title body-semi-bold-s">
                  {
                    this.#componentLocale.tabBasic.frontEnd
                      .backendOnlyCalloutTitle
                  }
                </span>
                <span class="callout__description body-regular-xs">
                  {
                    this.#componentLocale.tabBasic.frontEnd
                      .backendOnlyCalloutDescription
                  }
                </span>
              </div>
            </div>
          )}
        </div>
      </section>
    ];
  };

  #runtimeEnvironmentChangedHandler = async (
    event: ChRadioGroupRenderCustomEvent<string>
  ): Promise<void> => {
    this.#selectedRuntimeEnvironmentId = event.detail;
    await this.#updateLanguages();
    await this.#updateDataSources();
    this.#updateEnvironmentName();
  };

  #createCallbackHandler = async () => {
    const selectedFrontEndIds =
      this.#frontEndsCheckedMap.get(this.#selectedLanguageId) ?? [];

    this.createCallback({
      setAsCurrent: !!this.#setAsCurrentEnvironmentEl?.value,
      name: this.#environmentNameEl.value,
      runtime: this.#selectedRuntimeEnvironmentId,
      language: this.#languageEl.value,
      dataStores: this.#dataStoreEl.value,
      frontEnds: selectedFrontEndIds
    }).then((formSubmitResult: FormSubmitResult) => {
      this.validatableControls = validateControls(
        formSubmitResult,
        this.validatableControls
      );
    });
  };

  #tabSelectionChangedHandler = (
    event: CustomEvent<{
      lastSelectedIndex: number;
      newSelectedId: string;
      newSelectedIndex: number;
    }>
  ): void => {
    if (event.detail.newSelectedId === ADVANCED_TAB_SELECTOR) {
      this.advancedTabIsVisible = true;
      // Some logic for advanced tab is done on componentDidRender lifecycle,
      // since the reference to the elements is needed.
    }
  };

  #updateDataSources = async () => {
    const updatedDataSources = await this.getDataSourcesCallback(
      this.#selectedLanguageId,
      this.#selectedRuntimeEnvironmentId
    );
    if (updatedDataSources?.length > 0) {
      this.dataStores = updatedDataSources;
    }
  };

  #updateEnvironmentName = async () => {
    if (!this.#envNameModifiedByUser) {
      this.environmentName = await this.getEnvironmentNameCallback(
        this.#selectedLanguageId,
        this.#selectedDataStoreId,
        this.#selectedRuntimeEnvironmentId
      );
    }
  };

  #updateFrontEnds = async () => {
    const updatedFrontEnds = await this.getFrontEndsCallback(
      this.#selectedLanguageId
    );
    if (updatedFrontEnds?.length > 0) {
      this.frontEnds = updatedFrontEnds;

      // Seed the checked map from the data's checked property so that
      // initially checked items render as checked on first load.
      if (!this.#frontEndsCheckedMap.has(this.#selectedLanguageId)) {
        const checkedIds = updatedFrontEnds
          .filter(fe => fe.checked)
          .map(fe => fe.value || fe.id);

        if (checkedIds.length > 0) {
          this.#frontEndsCheckedMap.set(this.#selectedLanguageId, checkedIds);
        }
      }
    }
  };

  #updateLanguages = async () => {
    const updatedLanguages = await this.getLanguagesCallback(
      this.#selectedRuntimeEnvironmentId
    );
    if (updatedLanguages?.length > 0) {
      this.languages = updatedLanguages;
    }
  };

  #frontEndCheckboxInputHandler = (
    event: ChCheckboxCustomEvent<string> | InputEvent
  ) => {
    const checkedValue = (event.target as HTMLChCheckboxElement).checkedValue;
    const checked = event.detail === checkedValue;
    this.#updateFrontEndsCheckedMap(checkedValue, checked);
  };

  /**
   * This function updates frontEndsCheckedMap, that contains the checked
   * checkboxes for each language front ends.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #updateFrontEndsCheckedMap = (checkboxId: string, checked: boolean) => {
    let currentState = this.#frontEndsCheckedMap.get(this.#selectedLanguageId);
    if (!currentState) {
      // first time
      currentState = [];
      this.#frontEndsCheckedMap.set(this.#selectedLanguageId, currentState);
    }
    if (checked) {
      // update by adding
      currentState.push(checkboxId);
    } else {
      // update by removing
      const checkboxIndex = currentState.findIndex(id => id === checkboxId);
      removeIndex(currentState, checkboxIndex);
    }
  };

  render() {
    return (
      <Host class="widget scrollable">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>

        <section class="section">
          <header
            class={{
              "header": true,
              "spacing-body-inline": true,
              "spacing-body-block-start": true,
              "dialog-header": this.isAdvanced,
              "dialog-header-with-border": !this.isAdvanced
            }}
          >
            <div
              // #environment name
              class="field field-inline"
            >
              <label class="label" htmlFor="environment-name">
                {this.#componentLocale.name}
              </label>
              <ch-edit
                // environment name
                autoFocus
                id="environment-name"
                class="input"
                part="environment-name"
                value={this.environmentName}
                placeholder={this.#componentLocale.environmentNamePlaceholder}
                // TODO: toolTip={config.tooltip}
                ref={(el: HTMLChEditElement) =>
                  (this.#environmentNameEl = el as HTMLChEditElement)
                }
                onInput={this.#envNameInputHandler}
              ></ch-edit>
            </div>
          </header>

          {this.isAdvanced ? (
            <ch-tab-render
              // main section
              class={{
                tab: true,
                [MAIN_SECTION_CLASSES]: true
              }}
              tabListPosition="block-start"
              model={this.#newEnvironmentSectionsModel}
              selectedId={BASIC_TAB_SELECTOR}
              overflow="auto"
              contain="size"
              onSelectedItemChange={
                !this.advancedTabIsVisible && this.#tabSelectionChangedHandler
              }
            >
              <div
                slot={BASIC_TAB_SELECTOR}
                class={BASIC_SECTION_PARENT_CLASSES}
                part="tab-button-basic"
              >
                {this.#renderBasic()}
              </div>
              {this.advancedTabIsVisible && (
                <div
                  slot={ADVANCED_TAB_SELECTOR}
                  class="advanced spacing-body-inline"
                  part="tab-button-advanced"
                >
                  {this.#renderAdvanced()}
                </div>
              )}
            </ch-tab-render>
          ) : (
            <div
              // main section
              class={MAIN_SECTION_CLASSES}
            >
              <div class={BASIC_SECTION_PARENT_CLASSES}>
                {this.#renderBasic()}
              </div>
            </div>
          )}

          <footer
            class={{
              "dialog-footer": this.isAdvanced,
              "dialog-footer-with-border": !this.isAdvanced,
              "dialog-footer-space-between": true,
              "spacing-body-block-end": true,
              "spacing-body-inline": true
            }}
          >
            <ch-checkbox
              // #set as current environment
              class="checkbox"
              caption={this.#componentLocale.footer.setAsCurrentEnvironment}
              ref={(el: HTMLChCheckboxElement) =>
                (this.#setAsCurrentEnvironmentEl = el as HTMLChCheckboxElement)
              }
              checkedValue="current-environment"
              value={this.setAsTarget ? "current-environment" : ""}
              part="set-as-current-environment"
            ></ch-checkbox>

            <div class="buttons-spacer">
              <button
                // #cancel
                class="button-secondary"
                onClick={this.cancelCallback}
                part="button button-cancel"
              >
                {this.#componentLocale.footer.btnCancel}
              </button>

              <button
                // #create
                class="button-primary"
                onClick={this.#createCallbackHandler}
                part="button button-create"
              >
                {this.#componentLocale.footer.btnCreate}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type CancelCallback = () => Promise<void>;

export type CreateCallback = (
  data: NewEnvironmentData
) => Promise<FormSubmitResult>;

export type GetDataSourcesCallback = (
  selectedLanguageId: string,
  selectedRuntimeId: string
) => Promise<GxOption[]>;

export type GetEnvironmentNameCallback = (
  selectedLanguageId: string,
  selectedDataSourceId: string,
  selectedRuntimeId: string
) => Promise<string | undefined>;

export type GetFrontEndsCallback = (
  selectedLanguageId: string
) => Promise<GxOption[]>;

export type GetLanguagesCallback = (
  selectedRuntimeId: string
) => Promise<GxOption[]>;

export interface NewEnvironmentData {
  setAsCurrent: boolean;
  name: string;
  runtime: string;
  language: string;
  dataStores: string;
  frontEnds: string[];
}
